/**
 * Lenavio Watchdog — admin ops API.
 *
 * Platform-admin only — the user must have ``is_platform_admin =
 * true`` on their User row. The backend enforces this; the frontend
 * route is gated on the same flag.
 */
import client from "./client";

export type OpsSeverity = "page" | "warn" | "info";

export interface OpsAlertRow {
  id: string;
  signal_key: string;
  severity: OpsSeverity;
  title: string;
  summary: string | null;
  suggested_action: string | null;
  signal_data: Record<string, unknown> | null;
  affected_orgs:
    | Array<{
        id?: string;
        slug?: string;
        name?: string;
        metric?: string;
      }>
    | null;
  fanout_sent: boolean;
  acknowledged: boolean;
  acknowledged_at: string | null;
  created_at: string;
}

export interface OpsHealth {
  open_pages: number;
  open_warns: number;
  open_infos: number;
  last_alert_at: string | null;
  severity: "ok" | "degraded" | "down";
}

export interface OpsRunResult {
  fired: number;
  paged: number;
  skipped_dedup: string[];
  collector_errors: string[];
  error: string | null;
}

export interface ListAlertsFilters {
  severity?: OpsSeverity;
  acknowledged?: boolean;
  limit?: number;
}

export async function listOpsAlerts(
  filters: ListAlertsFilters = {},
): Promise<OpsAlertRow[]> {
  const params: Record<string, string | number | boolean> = {};
  if (filters.severity) params.severity = filters.severity;
  if (filters.acknowledged !== undefined)
    params.acknowledged = filters.acknowledged;
  params.limit = filters.limit ?? 100;
  const res = await client.get<OpsAlertRow[]>("/admin/ops/alerts", { params });
  return res.data;
}

export async function ackOpsAlert(id: string): Promise<OpsAlertRow> {
  const res = await client.post<OpsAlertRow>(
    `/admin/ops/alerts/${id}/ack`,
  );
  return res.data;
}

export async function runOpsWatchdog(): Promise<OpsRunResult> {
  const res = await client.post<OpsRunResult>("/admin/ops/run");
  return res.data;
}

export async function getOpsHealth(): Promise<OpsHealth> {
  const res = await client.get<OpsHealth>("/admin/ops/health");
  return res.data;
}
